import {DestroyRef, Directive, effect, inject, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Observable} from 'rxjs';
import {FormMode} from '../../shared/types/common';
import {UtilService} from '../../services/util.service';
import {ConfirmTypeToDeleteModalComponent} from '../../shared/components/confirm-type-to-delete-modal/confirm-type-to-delete-modal.component';

export interface ConfirmDeleteConfig {
  header: string;
  description: string;
  confirmLabel: string;
  expectedValue: string;
}

/**
 * Shared scaffolding for the management-screen form drawers (account/user/brand). Owns the bits every
 * drawer repeated verbatim: the `saving` flag (and the disable-close lock it drives), the entity +
 * close callback handed in through the drawer data, the snackbar success/error handlers, and the
 * type-to-confirm delete flow.
 *
 * Each drawer keeps its own form, configs and submit() — those legitimately differ per entity (the
 * brand drawer's submit runs an extra domain-conflict check first), so they are intentionally NOT
 * pulled up here. Subclasses must call super() and provide the form themselves.
 */
@Directive()
export abstract class DrawerFormBase<E> {
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly utilService = inject(UtilService);
  protected readonly dialog = inject(MatDialog);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);

  protected readonly FormMode = FormMode;
  public currentMode: FormMode = FormMode.ADD;
  public readonly saving = signal(false);

  // Supplied by the opener (management-screen) via the drawer data: the row being edited (absent in
  // add mode) and a callback that runs the drawer's animated close.
  protected readonly entity = this.data?.data?.entity as E | undefined;
  protected readonly close: () => void = (this.data?.data?.close as () => void) ?? (() => {});

  constructor() {
    // Lock the drawer (backdrop-click + Escape) while a save/delete is in flight so it can't be
    // dismissed mid-request; programmatic close() on success/cancel still works.
    effect(() => (this.dialogRef.disableClose = this.saving()));
  }

  cancel(): void {
    this.close();
  }

  protected onSuccess(message: string): void {
    this.utilService.openSnackBar(message, {position: 'center', variant: 'success'});
    this.close();
  }

  protected onError(error: unknown): void {
    this.saving.set(false);
    const message = (error as {error?: {message?: string}})?.error?.message ?? 'Something went wrong. Please try again.';
    this.utilService.openSnackBar(message, {variant: 'error', position: 'center'});
  }

  // Opens the type-to-confirm delete dialog; on confirmation, runs the delete request with the
  // standard saving / success / error handling. Used by the account and user drawers.
  protected confirmAndDelete(config: ConfirmDeleteConfig, request: () => Observable<unknown>, successMessage: string): void {
    const dialogRef = this.dialog.open(ConfirmTypeToDeleteModalComponent, {
      data: config,
      panelClass: 'management-modal',
      autoFocus: false,
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }
      this.saving.set(true);
      request()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.onSuccess(successMessage),
          error: error => this.onError(error),
        });
    });
  }
}
